import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Mail, ArrowLeft, Plus, Copy, Trash2, Save, AlertTriangle, ChevronDown, ChevronRight, Code, Eye, Upload, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import JSZip from 'jszip';
import { useQueryClient } from '@tanstack/react-query';
import { useEmailTemplates, useEmailTemplate, usePlaceholderInfo } from '../hooks/useApi';
import { useEnv } from '../contexts/EnvContext';
import { api } from '../api/client';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import { StatusBadge } from '../components/shared/StatusBadge';
import type { EmailTemplateOverview } from '../types/api';

const ALL_LANGUAGES = [
  'bg', 'cs', 'da', 'de', 'el', 'en', 'es', 'et', 'fi', 'fr',
  'ga', 'hr', 'hu', 'it', 'lt', 'lv', 'mt', 'nl', 'no', 'pl',
  'pt', 'ro', 'sk', 'sl', 'sr', 'sv', 'tr', 'uk',
];

const CORE_LANGUAGES = ['en', 'pl', 'de'];

/** Extract email metadata from HTML meta tags */
function extractEmailMeta(html: string): {
  subject?: string;
  fromDisplayName?: string;
  replyTo?: string;
} {
  const result: Record<string, string> = {};
  const metaMap: Record<string, string> = {
    'email-subject': 'subject',
    'email-from': 'fromDisplayName',
    'email-reply-to': 'replyTo',
  };
  for (const [metaName, field] of Object.entries(metaMap)) {
    const match = html.match(new RegExp(`<meta\\s+name=["']${metaName}["']\\s+content=["']([^"']+)["']`, 'i'))
      || html.match(new RegExp(`<meta\\s+content=["']([^"']+)["']\\s+name=["']${metaName}["']`, 'i'));
    if (match) {
      result[field] = match[1];
    }
  }
  return result;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'teraz';
  if (diffMinutes < 60) return `${diffMinutes}min temu`;
  if (diffHours < 24) return `${diffHours}h temu`;
  if (diffDays < 30) return `${diffDays}d temu`;
  return date.toLocaleDateString('pl-PL');
}

// ---- List View ----

function TemplateCard({
  template,
  onClick,
}: {
  template: EmailTemplateOverview;
  onClick: () => void;
}) {
  const existingLanguages = new Set(
    template.languages.filter((l) => l.exists).map((l) => l.language)
  );

  const latestUpdate = template.languages
    .filter((l) => l.exists && l.updateDate)
    .sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime())[0];

  return (
    <button
      type="button"
      onClick={onClick}
      className="card w-full text-left hover:ring-2 hover:ring-brand-300 dark:hover:ring-brand-600 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {template.templateName}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
            {template.description}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {template.languageCount}/{template.totalLanguages} jezykow
          </span>
          {latestUpdate && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {formatRelativeTime(latestUpdate.updateDate)}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mt-3">
        {ALL_LANGUAGES.map((lang) => {
          const exists = existingLanguages.has(lang);
          const isCoreLanguage = CORE_LANGUAGES.includes(lang);
          return (
            <span
              key={lang}
              className={`inline-block w-2 h-2 rounded-full ${
                exists
                  ? 'bg-emerald-500'
                  : isCoreLanguage
                    ? 'bg-red-400'
                    : 'bg-gray-300 dark:bg-gray-600'
              }`}
              title={lang}
            />
          );
        })}
      </div>
    </button>
  );
}

function TemplateListView({
  onSelect,
}: {
  onSelect: (template: EmailTemplateOverview) => void;
}) {
  const { data, isLoading, error, refetch } = useEmailTemplates();

  if (isLoading) return <LoadingSpinner label="Ladowanie szablonow..." />;
  if (error) return <ErrorAlert message={error.message} onRetry={() => refetch()} />;
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        Brak szablonow email
      </div>
    );
  }

  const issuerTemplates = data.filter((t) => t.database === 'issuer');
  const walletTemplates = data.filter((t) => t.database === 'wallet');

  return (
    <div className="space-y-8">
      {issuerTemplates.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            ISSUER
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {issuerTemplates.map((t) => (
              <TemplateCard key={`${t.database}-${t.templateName}`} template={t} onClick={() => onSelect(t)} />
            ))}
          </div>
        </section>
      )}
      {walletTemplates.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            WALLET
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {walletTemplates.map((t) => (
              <TemplateCard key={`${t.database}-${t.templateName}`} template={t} onClick={() => onSelect(t)} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ---- Edit View ----

function TemplateEditView({
  template,
  onBack,
}: {
  template: EmailTemplateOverview;
  onBack: () => void;
}) {
  const { env } = useEnv();
  const queryClient = useQueryClient();

  const existingLanguages = useMemo(
    () => template.languages.filter((l) => l.exists).map((l) => l.language),
    [template.languages]
  );

  const [selectedLanguage, setSelectedLanguage] = useState(() =>
    existingLanguages.includes('en') ? 'en' : existingLanguages[0] || 'en'
  );
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fromDisplayName, setFromDisplayName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddLanguage, setShowAddLanguage] = useState(false);
  const [showEnReference, setShowEnReference] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data: templateDetail, isLoading: isLoadingDetail } = useEmailTemplate(
    template.database,
    template.templateName,
    selectedLanguage
  );

  const { data: enDetail } = useEmailTemplate(
    template.database,
    template.templateName,
    'en'
  );

  const { data: placeholderInfo } = usePlaceholderInfo(template.templateName);

  // Sync form when template detail loads
  useEffect(() => {
    if (templateDetail) {
      setSubject(templateDetail.subject);
      setBody(templateDetail.body);
      setFromDisplayName(templateDetail.fromDisplayName || '');
      setReplyTo(templateDetail.replyTo || '');
    }
  }, [templateDetail]);

  // Placeholder validation
  const placeholderValidation = useMemo(() => {
    if (!placeholderInfo) return { found: [] as string[], missing: [] as string[] };
    const found: string[] = [];
    const missing: string[] = [];
    for (const placeholder of placeholderInfo.required) {
      const token = `[:${placeholder}:]`;
      if (body.includes(token) || subject.includes(token)) {
        found.push(placeholder);
      } else {
        missing.push(placeholder);
      }
    }
    return { found, missing };
  }, [placeholderInfo, body, subject]);

  // Preview HTML with sample values replaced
  const previewHtml = useMemo(() => {
    if (!body) return '';
    let html = body;
    if (placeholderInfo?.sampleValues) {
      for (const [key, value] of Object.entries(placeholderInfo.sampleValues)) {
        html = html.replaceAll(`[:${key}:]`, value);
      }
    }
    return html;
  }, [body, placeholderInfo]);

  const availableLanguages = ALL_LANGUAGES.filter(
    (l) => !existingLanguages.includes(l)
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.html') || file.name.endsWith('.htm'))) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const html = (ev.target?.result as string) || '';
        setBody(html);
        const meta = extractEmailMeta(html);
        if (meta.subject) setSubject(meta.subject);
        if (meta.fromDisplayName) setFromDisplayName(meta.fromDisplayName);
        if (meta.replyTo) setReplyTo(meta.replyTo);
      };
      reader.readAsText(file);
    }
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await api.saveEmailTemplate(
        template.database,
        template.templateName,
        selectedLanguage,
        {
          subject,
          body,
          ...(fromDisplayName ? { fromDisplayName } : {}),
          ...(replyTo ? { replyTo } : {}),
        },
        env
      );
      await queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      await queryClient.invalidateQueries({
        queryKey: ['email-template', template.database, template.templateName, selectedLanguage],
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Blad zapisu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setSaveError(null);
    try {
      await api.deleteEmailTemplate(
        template.database,
        template.templateName,
        selectedLanguage,
        env
      );
      await queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setShowDeleteConfirm(false);
      // Switch to EN or first available
      const remaining = existingLanguages.filter((l) => l !== selectedLanguage);
      if (remaining.length > 0) {
        setSelectedLanguage(remaining.includes('en') ? 'en' : remaining[0]);
      } else {
        onBack();
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Blad usuwania');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyFromEn = () => {
    if (enDetail) {
      setSubject(enDetail.subject);
      setBody(enDetail.body);
      setFromDisplayName(enDetail.fromDisplayName || '');
      setReplyTo(enDetail.replyTo || '');
    }
  };

  const handleAddLanguage = (lang: string) => {
    setSelectedLanguage(lang);
    setSubject('');
    setBody('');
    setFromDisplayName('');
    setReplyTo('');
    setShowAddLanguage(false);
  };

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={onBack} className="btn-secondary text-sm flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" />
          Powrot
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
            {template.templateName}
          </h2>
          <StatusBadge
            label={template.database.toUpperCase()}
            variant={template.database === 'issuer' ? 'info' : 'success'}
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="input-field text-sm py-1.5 w-24"
          >
            {existingLanguages.map((lang) => (
              <option key={lang} value={lang}>
                {lang.toUpperCase()}
              </option>
            ))}
            {!existingLanguages.includes(selectedLanguage) && (
              <option value={selectedLanguage}>
                {selectedLanguage.toUpperCase()} (nowy)
              </option>
            )}
          </select>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowAddLanguage(!showAddLanguage)}
              className="btn-secondary text-sm flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Dodaj tlumaczenie
            </button>
            {showAddLanguage && (
              <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto w-32">
                {availableLanguages.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">Wszystkie jezyki dodane</div>
                ) : (
                  availableLanguages.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => handleAddLanguage(lang)}
                      className="block w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          {selectedLanguage !== 'en' && enDetail && (
            <button type="button" onClick={handleCopyFromEn} className="btn-secondary text-sm flex items-center gap-1">
              <Copy className="h-4 w-4" />
              Kopiuj z EN
            </button>
          )}
        </div>
      </div>

      {isLoadingDetail && existingLanguages.includes(selectedLanguage) ? (
        <LoadingSpinner size="sm" />
      ) : (
        <>
          {/* Form fields */}
          <div className="card space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="input-field w-full"
                placeholder="Temat wiadomosci..."
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  From Display Name
                  <span className="text-gray-400 ml-1">(opcjonalne)</span>
                </label>
                <input
                  type="text"
                  value={fromDisplayName}
                  onChange={(e) => setFromDisplayName(e.target.value)}
                  className="input-field w-full"
                  placeholder="np. Credentium"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reply-To
                  <span className="text-gray-400 ml-1">(opcjonalne)</span>
                </label>
                <input
                  type="text"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  className="input-field w-full"
                  placeholder="np. support@credentium.com"
                />
              </div>
            </div>
          </div>

          {/* Editor + Preview */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Tresc HTML
              </h3>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="btn-secondary text-xs flex items-center gap-1"
              >
                {showPreview ? (
                  <>
                    <Code className="h-3.5 w-3.5" />
                    Pokaz kod
                  </>
                ) : (
                  <>
                    <Eye className="h-3.5 w-3.5" />
                    Pokaz podglad
                  </>
                )}
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Editor */}
              <div
                className="relative"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="input-field w-full font-mono text-xs leading-relaxed min-h-[400px] resize-y"
                  placeholder="Wklej lub przeciagnij plik HTML..."
                  spellCheck={false}
                />
                {isDragging && (
                  <div className="absolute inset-0 bg-brand-500/10 border-2 border-dashed border-brand-500 rounded-lg flex items-center justify-center z-10">
                    <p className="text-brand-600 dark:text-brand-400 font-medium">
                      Upusc plik HTML tutaj
                    </p>
                  </div>
                )}
              </div>
              {/* Preview */}
              {showPreview && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white">
                  <iframe
                    srcDoc={previewHtml}
                    sandbox=""
                    className="w-full min-h-[400px] border-0"
                    title="Podglad szablonu"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Zip Upload */}
          <ZipUpload
            database={template.database}
            templateName={template.templateName}
            subject={subject}
            fromDisplayName={fromDisplayName}
            replyTo={replyTo}
            onComplete={() => {
              queryClient.invalidateQueries({ queryKey: ['email-templates'] });
              queryClient.invalidateQueries({ queryKey: ['email-template'] });
            }}
          />

          {/* Placeholder validation */}
          {placeholderInfo && placeholderInfo.required.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Placeholdery
              </h3>
              <div className="flex flex-wrap gap-2">
                {placeholderValidation.found.map((p) => (
                  <StatusBadge key={p} label={`[:${p}:]`} variant="success" />
                ))}
                {placeholderValidation.missing.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-300 dark:border-red-800"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    [:{ p }:]
                  </span>
                ))}
              </div>
              {placeholderValidation.missing.length > 0 && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                  Brakuje {placeholderValidation.missing.length} placeholderow
                </p>
              )}
            </div>
          )}

          {/* English reference */}
          {selectedLanguage !== 'en' && enDetail && (
            <div className="card">
              <button
                type="button"
                onClick={() => setShowEnReference(!showEnReference)}
                className="flex items-center gap-2 w-full text-left"
              >
                {showEnReference ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Wersja angielska (referencja)
                </h3>
              </button>
              {showEnReference && (
                <div className="mt-3 space-y-3">
                  <div>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Subject:</span>
                    <p className="text-sm text-gray-900 dark:text-white mt-0.5">{enDetail.subject}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Body:</span>
                    <pre className="mt-1 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 overflow-x-auto max-h-64 overflow-y-auto font-mono">
                      {enDetail.body}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !subject || !body}
                className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Zapisywanie...' : 'Zapisz'}
              </button>
              {selectedLanguage !== 'en' && existingLanguages.includes(selectedLanguage) && (
                <>
                  {showDeleteConfirm ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-600 dark:text-red-400">Na pewno?</span>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="btn-danger text-xs"
                      >
                        {isDeleting ? 'Usuwanie...' : 'Tak, usun'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        className="btn-secondary text-xs"
                      >
                        Anuluj
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="btn-danger text-sm flex items-center gap-1.5"
                    >
                      <Trash2 className="h-4 w-4" />
                      Usun tlumaczenie
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              {saveSuccess && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">Zapisano</span>
              )}
              {templateDetail?.updateDate && (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Ostatnia zmiana: {new Date(templateDetail.updateDate).toLocaleString('pl-PL')}
                </span>
              )}
            </div>
          </div>

          {saveError && (
            <ErrorAlert message={saveError} />
          )}
        </>
      )}
    </div>
  );
}

// ---- Zip Upload ----

interface ZipUploadResult {
  language: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

function ZipUpload({
  database,
  templateName,
  subject,
  fromDisplayName,
  replyTo,
  onComplete,
}: {
  database: string;
  templateName: string;
  subject: string;
  fromDisplayName: string;
  replyTo: string;
  onComplete: () => void;
}) {
  const { env } = useEnv();
  const [results, setResults] = useState<ZipUploadResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDraggingZip, setIsDraggingZip] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function extractLanguage(filename: string): string | null {
    const name = filename.replace(/\.(html?|htm)$/i, '');
    // Try suffix: template_pl, template-pl, or just "pl"
    const suffixMatch = name.match(/[_-]([a-z]{2})$/i);
    if (suffixMatch) {
      const lang = suffixMatch[1].toLowerCase();
      if (ALL_LANGUAGES.includes(lang)) return lang;
    }
    // Try standalone: "pl.html"
    const standaloneMatch = name.match(/^([a-z]{2})$/i);
    if (standaloneMatch) {
      const lang = standaloneMatch[1].toLowerCase();
      if (ALL_LANGUAGES.includes(lang)) return lang;
    }
    return null;
  }

  async function processZip(file: File) {
    setIsProcessing(true);
    setResults([]);

    try {
      const zip = await JSZip.loadAsync(file);
      const entries: { language: string; content: string; meta: ReturnType<typeof extractEmailMeta> }[] = [];

      for (const [path, zipEntry] of Object.entries(zip.files)) {
        if (zipEntry.dir) continue;
        const filename = path.split('/').pop() || '';
        if (!filename.match(/\.html?$/i)) continue;

        const lang = extractLanguage(filename);
        if (!lang) continue;

        const content = await zipEntry.async('text');
        const meta = extractEmailMeta(content);
        entries.push({ language: lang, content, meta });
      }

      if (entries.length === 0) {
        setResults([{ language: '-', status: 'error', error: 'Brak plikow HTML z rozpoznanym jezykiem (np. _pl.html, _de.html)' }]);
        setIsProcessing(false);
        return;
      }

      // Init results
      const initialResults: ZipUploadResult[] = entries.map((e) => ({
        language: e.language,
        status: 'pending',
      }));
      setResults(initialResults);

      // Upload each
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        setResults((prev) =>
          prev.map((r, idx) => (idx === i ? { ...r, status: 'uploading' } : r))
        );

        try {
          const fileSubject = entry.meta.subject || subject || `${templateName} (${entry.language})`;
          const fileFrom = entry.meta.fromDisplayName || fromDisplayName;
          const fileReplyTo = entry.meta.replyTo || replyTo;
          await api.saveEmailTemplate(
            database,
            templateName,
            entry.language,
            {
              subject: fileSubject,
              body: entry.content,
              ...(fileFrom ? { fromDisplayName: fileFrom } : {}),
              ...(fileReplyTo ? { replyTo: fileReplyTo } : {}),
            },
            env
          );
          setResults((prev) =>
            prev.map((r, idx) => (idx === i ? { ...r, status: 'success' } : r))
          );
        } catch (err) {
          setResults((prev) =>
            prev.map((r, idx) => (idx === i ? { ...r, status: 'error', error: err instanceof Error ? err.message : 'Blad' } : r))
          );
        }
      }

      onComplete();
    } catch (err) {
      setResults([{ language: '-', status: 'error', error: 'Nie udalo sie odczytac pliku ZIP' }]);
    } finally {
      setIsProcessing(false);
    }
  }

  const handleZipDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingZip(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.zip')) {
      processZip(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processZip(file);
    e.target.value = '';
  };

  const successCount = results.filter((r) => r.status === 'success').length;
  const errorCount = results.filter((r) => r.status === 'error').length;

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
        <Upload className="h-4 w-4" />
        Masowy upload z ZIP
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Wrzuc plik ZIP z plikami HTML. Nazwy plikow musza konczyc sie kodem jezyka, np.{' '}
        <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">template_pl.html</code>,{' '}
        <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">de.html</code>.
        Subject, From i Reply-To zostana skopiowane z biezacych pol formularza.
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setIsDraggingZip(true); }}
        onDragLeave={() => setIsDraggingZip(false)}
        onDrop={handleZipDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDraggingZip
            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-brand-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Upload className={`h-8 w-8 mx-auto mb-2 ${isDraggingZip ? 'text-brand-500' : 'text-gray-400'}`} />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isProcessing ? 'Przetwarzanie...' : 'Przeciagnij plik ZIP lub kliknij'}
        </p>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {results.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {r.status === 'pending' && <div className="w-4 h-4 rounded-full bg-gray-300 dark:bg-gray-600" />}
              {r.status === 'uploading' && <Loader2 className="w-4 h-4 text-brand-500 animate-spin" />}
              {r.status === 'success' && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
              {r.status === 'error' && <XCircle className="w-4 h-4 text-red-500" />}
              <span className="font-mono text-xs font-bold text-gray-700 dark:text-gray-300 w-6">
                {r.language.toUpperCase()}
              </span>
              {r.status === 'error' && (
                <span className="text-xs text-red-500">{r.error}</span>
              )}
              {r.status === 'success' && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">Zapisano</span>
              )}
            </div>
          ))}
          {!isProcessing && results.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {successCount > 0 && <span className="text-emerald-600 dark:text-emerald-400">{successCount} zapisanych</span>}
              {successCount > 0 && errorCount > 0 && ', '}
              {errorCount > 0 && <span className="text-red-500">{errorCount} bledow</span>}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Main Page ----

export function EmailTemplatesPage() {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateOverview | null>(null);

  return (
    <div className="space-y-6">
      {!selectedTemplate && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Mail className="h-6 w-6 text-brand-600 dark:text-brand-400" />
            Szablony email
          </h1>
          <p className="text-sm text-gray-500 mt-1">Zarzadzanie szablonami wiadomosci email</p>
        </div>
      )}

      {selectedTemplate ? (
        <TemplateEditView
          template={selectedTemplate}
          onBack={() => setSelectedTemplate(null)}
        />
      ) : (
        <TemplateListView onSelect={setSelectedTemplate} />
      )}
    </div>
  );
}
